import type { NextRequest } from "@/modules/fetch/types"
import type { I18nLocale } from "@/modules/i18n/types"

import { isWwwCareerCenter } from "@/modules/careerCenter/isWwwCareerCenter"
import { getCareerCenter } from "@/modules/careerCenter/services/getCareerCenter"
import { getCsSpotlightPage } from "@/modules/csSpotlight/api/getCsSpotlightPage"
import { PUBLIC_LOCALES } from "@/modules/locales/constants"
import { getGuidanceTools } from "@/modules/profile/api/getGuidanceTools"
import { getGuidanceUrl } from "@/modules/profile/utils/getGuidanceUrl"
import { getCurrentUser } from "@/modules/user/api/getCurrentUser"

export type CareerCenterModules = {
  // Module flags
  hasAdvices: boolean
  hasAppointments: boolean
  hasCareerExplorer: boolean
  hasCareerServiceSpotlight: boolean
  hasEvents: boolean
  hasFreya: boolean
  hasHester: boolean
  hasInsights: boolean
  hasJobOffers: boolean
  hasJobRoles: boolean
  hasMainCareerCenterRessources: boolean
  hasMarco: boolean
  hasRessources: boolean
  hasVasco: boolean

  // Guidance tool URLs
  freyaUrl: string
  hesterUrl: string
  marcoUrl: string
  vascoUrl: string

  // Resources names
  resourcesModuleLocalizedName: string
  mainCareerCenterResourcesModuleLocalizedName: string
}

export const getCareerCenterModules = async ({
  locale,
  permalink,
  host,
  nextRequest,
}: {
  locale: I18nLocale
  permalink: string
  host: string
  nextRequest?: NextRequest
}): Promise<CareerCenterModules> => {
  const [careerCenter, currentUser, guidanceTools] = await Promise.all([
    getCareerCenter({ locale, permalink, req: nextRequest }),
    getCurrentUser({ hostname: host, locale, req: nextRequest }),
    getGuidanceTools({ nextRequest }).catch(() => ({
      availableGuidanceTools: [],
      freyaResult: undefined,
      hesterResult: undefined,
      marcoResult: undefined,
      vascoResult: undefined,
    })),
  ])

  const csSpotlightPage = await getCsSpotlightPage({
    careerCenterId: careerCenter.id,
    locale,
    req: nextRequest,
  }).catch(() => null)

  const isWww = isWwwCareerCenter(careerCenter)
  const { modules: careerCenterModules } = careerCenter.careerCenterConf
  const { attributes: userAttributes } = currentUser
  const { availableGuidanceTools } = guidanceTools
  const isUserAdmin = !!(userAttributes.isAdmin || userAttributes.isCcSuperAdmin)

  const getGuidanceToolUrl = (toolName: "freya" | "marco" | "vasco" | "hester"): string => {
    const toolResults = guidanceTools[`${toolName}Result`]
    const isTestComplete = !!toolResults
    return getGuidanceUrl({
      guidanceToolName: toolName,
      isAdmin: isUserAdmin,
      permalink,
      redirectPage: isTestComplete ? "/last-results" : "/account",
    })
  }

  const hasEvents = !!careerCenterModules.hasEvents
  const hasJobOffers = !!careerCenterModules.hasJobOffers

  const hasAdvices = isWww ? PUBLIC_LOCALES.includes(locale) : careerCenterModules.hasAdvices
  /*
   * The front-office Appointments module requires two configuration options:
   * 1. hasAppointmentsLaunchedInFo (enabled by default)
   * 2. hasAppointments (the main Appointments configuration)
   * Both must be true for the front-office Appointments module to be available.
   */
  const hasAppointments = careerCenterModules.hasAppointments && careerCenterModules.hasAppointmentsLaunchedInFo
  const hasCareerExplorer = careerCenterModules.hasLearningPlatformAccess
  const hasMainCareerCenterRessources = !!careerCenter.mainCareerCenterConf?.hasMainCareerCenterArticles
  const hasRessources = careerCenterModules.hasArticles

  const hasJobRoles = !!careerCenterModules.hasGenericPositions && !!userAttributes.showJobRoles
  const hasInsights = !!(userAttributes.hasCommunityModule && userAttributes.hasCommunityInsights)
  const hasCareerServiceSpotlight = !!csSpotlightPage

  const hasFreya = availableGuidanceTools.some(tool => tool === "GUIDANCE_TOOL_FREYA")
  const hasHester = availableGuidanceTools.some(tool => tool === "GUIDANCE_TOOL_HESTER")
  const hasMarco = availableGuidanceTools.some(tool => tool === "GUIDANCE_TOOL_MARCO")
  const hasVasco = availableGuidanceTools.some(tool => tool === "GUIDANCE_TOOL_VASCO")

  const freyaUrl = hasFreya ? getGuidanceToolUrl("freya") : ""
  const hesterUrl = hasHester ? getGuidanceToolUrl("hester") : ""
  const marcoUrl = hasMarco ? getGuidanceToolUrl("marco") : ""
  const vascoUrl = hasVasco ? getGuidanceToolUrl("vasco") : ""

  const resourcesModuleLocalizedName = hasRessources ? careerCenter.careerCenterConf.customResourceLocale : ""
  const mainCareerCenterResourcesModuleLocalizedName = hasMainCareerCenterRessources
    ? (careerCenter.mainCareerCenterConf?.mainCareerCenterCustomResourceLocale ?? "")
    : ""

  return {
    freyaUrl,
    hasAdvices,
    hasAppointments,
    hasCareerExplorer,
    hasCareerServiceSpotlight,
    hasEvents,
    hasFreya,
    hasHester,
    hasInsights,
    hasJobOffers,
    hasJobRoles,
    hasMainCareerCenterRessources,
    hasMarco,
    hasRessources,
    hasVasco,
    hesterUrl,
    mainCareerCenterResourcesModuleLocalizedName,
    marcoUrl,
    resourcesModuleLocalizedName,
    vascoUrl,
  }
}
